import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, 
  ArrowRight,
  Shield,
  Award,
  Star,
  ChevronDown,
  Check,
  X,
  Sparkles,
  Brain,
  ChartBar,
  ClipboardList,
  Newspaper,
  CalendarClock,
  Timer,
  PlayCircle,
  Users,
  TrendingUp,
  Building2,
  Clock,
  Home,
  UserCheck,
  Building,
  BarChart3,
  MessageSquare,
  Zap,
  Play
} from 'lucide-react';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';
import { subscribeToNewsletter } from '../api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedComparison, setSelectedComparison] = useState('whenIWork');

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Comprehensive product features with screenshots
  const productFeatures = [
    {
      id: 'schedule',
      title: 'Intelligent Schedule Management',
      subtitle: 'Create perfect schedules in minutes, not hours',
      description: 'Our AI-powered scheduling engine takes the complexity out of workforce planning. Automatically assign shifts based on employee availability, skills, certifications, Employee laws, and business demands. Drag-and-drop interface makes adjustments effortless, while conflict detection prevents double-booking and compliance violations.',
      image: '/Schedule Page.png',
      icon: CalendarClock,
      benefits: [
        'AI-powered auto-scheduling saves 10+ hours per week',
        'Real-time conflict detection and resolution',
        'Template-based scheduling for recurring patterns',
        'Multi-location and multi-department support',
        'Employee cost forecasting and budget optimization',
        'Shift swap and trade management'
      ],
      stats: {
        time: '90% faster scheduling',
        accuracy: '99.8% conflict-free',
        adoption: '4.9/5 user rating'
      }
    },
    {
      id: 'timesheet',
      title: 'Advanced Timesheet & Attendance',
      subtitle: 'Accurate time tracking with complete accountability',
      description: 'Eliminate timesheet fraud and payroll errors with our comprehensive time tracking solution. GPS verification, facial recognition, and geofencing ensure employees clock in from authorized locations. Automatic break tracking, overtime calculations, and seamless payroll integration make payroll processing effortless and error-free.',
      image: '/Timesheet Page.png',
      icon: Timer,
      benefits: [
        'GPS and facial recognition clock-in verification',
        'Automatic overtime and break compliance tracking',
        'Mobile time clock with offline mode',
        'Real-time attendance monitoring dashboard',
        'Instant timesheet approval workflows',
        'Direct integration with 50+ payroll providers'
      ],
      stats: {
        time: '95% reduction in timesheet errors',
        accuracy: '100% location verification',
        adoption: 'Zero payroll disputes'
      }
    },
    {
      id: 'analytics',
      title: 'Powerful Analytics & Insights',
      subtitle: 'Data-driven decisions for optimal workforce performance',
      description: 'Transform raw scheduling data into actionable business intelligence. Track Employee costs, productivity trends, attendance patterns, and operational efficiency across all locations and departments. Customizable dashboards, automated reports, and predictive analytics help you optimize workforce allocation and reduce costs.',
      image: '/Analyticspage.png',
      icon: ChartBar,
      benefits: [
        'Real-time Employee cost analysis and forecasting',
        'Attendance and punctuality trend reports',
        'Productivity and efficiency metrics',
        'Overtime and compliance tracking',
        'Custom KPI dashboards for managers',
        'Automated weekly and monthly reporting'
      ],
      stats: {
        time: '35% average cost reduction',
        accuracy: '100% data accuracy',
        adoption: 'Instant insights'
      }
    },
    {
      id: 'admin',
      title: 'Comprehensive Admin Dashboard',
      subtitle: 'Complete control and visibility from one centralized hub',
      description: 'Manage your entire workforce from a single, intuitive dashboard. Monitor real-time attendance, approve time-off requests, handle shift swaps, track certifications, and oversee multi-location operations. Role-based permissions ensure data security while customizable widgets let you focus on what matters most to your business.',
      image: '/Admin Dashboard.png',
      icon: ClipboardList,
      benefits: [
        'Unified view of all locations and departments',
        'One-click approval workflows for requests',
        'Employee certification and training tracking',
        'Role-based access control and permissions',
        'Bulk actions for mass schedule updates',
        'Audit trail for all system changes'
      ],
      stats: {
        time: '80% faster admin tasks',
        accuracy: 'Full audit compliance',
        adoption: 'Enterprise-grade security'
      }
    },
    {
      id: 'newsfeed',
      title: 'Team Communication Hub',
      subtitle: 'Keep everyone connected and informed',
      description: 'Built-in communication tools ensure your team stays aligned and engaged. Share company updates, policy changes, important announcements, and shift notifications through a centralized newsfeed. Support for comments, reactions, and @mentions makes team colEmployeeation seamless, while read receipts ensure critical messages are acknowledged.',
      image: '/Newsfeed.png',
      icon: Newspaper,
      benefits: [
        'Company-wide announcements and updates',
        'Shift-specific messages and notifications',
        'File and document sharing capabilities',
        'Two-way communication with comments',
        'Read receipts for critical announcements',
        'Mobile push notifications for urgent updates'
      ],
      stats: {
        time: '90% message read rate',
        accuracy: 'Instant delivery',
        adoption: 'Zero missed updates'
      }
    },
    {
      id: 'ai',
      title: 'AI-Powered Intelligence',
      subtitle: 'Smart automation that learns and adapts',
      description: 'Leverage artificial intelligence to optimize your workforce operations. Our ML algorithms analyze historical data, predict staffing needs, suggest optimal schedules, detect patterns, and provide proactive recommendations. From predicting no-shows to optimizing shift coverage, AI helps you stay ahead of operational challenges.',
      image: '/Ai page.png',
      icon: Brain,
      benefits: [
        'Predictive staffing based on historical trends',
        'Automatic no-show and late pattern detection',
        'Smart shift recommendations for employees',
        'Demand forecasting for peak periods',
        'Anomaly detection for compliance issues',
        'Continuous learning and improvement'
      ],
      stats: {
        time: '50% better forecast accuracy',
        accuracy: '40% reduction in no-shows',
        adoption: 'Self-optimizing system'
      }
    }
  ];

  // Competitor comparison data
  const competitorComparison = {
    whenIWork: {
      name: 'When I Work',
      logo: Clock,
      features: [
        { feature: 'AI-Powered Scheduling', shiftry: true, competitor: false },
        { feature: 'Facial Recognition Clock-In', shiftry: true, competitor: false },
        { feature: 'GPS Geofencing', shiftry: true, competitor: true },
        { feature: 'Advanced Analytics Dashboard', shiftry: true, competitor: false },
        { feature: 'Multi-Location Support', shiftry: true, competitor: true },
        { feature: 'Payroll Integration', shiftry: true, competitor: true },
        { feature: 'Built-in Team Communication', shiftry: true, competitor: false },
        { feature: 'Predictive Staffing AI', shiftry: true, competitor: false },
        { feature: 'Certification Tracking', shiftry: true, competitor: false },
        { feature: '24/7 Customer Support', shiftry: true, competitor: false }
      ],
      pricing: { shiftry: '$2.50/user/mo', competitor: '$4.00/user/mo' }
    },
    deputy: {
      name: 'Deputy',
      logo: UserCheck,
      features: [
        { feature: 'AI-Powered Scheduling', shiftry: true, competitor: false },
        { feature: 'Facial Recognition Clock-In', shiftry: true, competitor: false },
        { feature: 'GPS Geofencing', shiftry: true, competitor: true },
        { feature: 'Advanced Analytics Dashboard', shiftry: true, competitor: true },
        { feature: 'Multi-Location Support', shiftry: true, competitor: true },
        { feature: 'Payroll Integration', shiftry: true, competitor: true },
        { feature: 'Built-in Team Communication', shiftry: true, competitor: false },
        { feature: 'Predictive Staffing AI', shiftry: true, competitor: false },
        { feature: 'Unlimited Users', shiftry: true, competitor: false },
        { feature: 'Free Mobile Apps', shiftry: true, competitor: true }
      ],
      pricing: { shiftry: '$2.50/user/mo', competitor: '$3.50/user/mo' }
    },
    homebase: {
      name: 'Homebase',
      logo: Home,
      features: [
        { feature: 'AI-Powered Scheduling', shiftry: true, competitor: false },
        { feature: 'Facial Recognition Clock-In', shiftry: true, competitor: false },
        { feature: 'GPS Geofencing', shiftry: true, competitor: false },
        { feature: 'Advanced Analytics Dashboard', shiftry: true, competitor: false },
        { feature: 'Multi-Location Support', shiftry: true, competitor: true },
        { feature: 'Payroll Integration', shiftry: true, competitor: true },
        { feature: 'Built-in Team Communication', shiftry: true, competitor: true },
        { feature: 'Certification Tracking', shiftry: true, competitor: false },
        { feature: 'Overtime Alerts', shiftry: true, competitor: true },
        { feature: 'Custom Branding', shiftry: true, competitor: false }
      ],
      pricing: { shiftry: '$2.50/user/mo', competitor: 'Free-$20/location' }
    },
    humanity: {
      name: 'Humanity (TCP Software)',
      logo: Users,
      features: [
        { feature: 'AI-Powered Scheduling', shiftry: true, competitor: false },
        { feature: 'Facial Recognition Clock-In', shiftry: true, competitor: false },
        { feature: 'GPS Geofencing', shiftry: true, competitor: true },
        { feature: 'Advanced Analytics Dashboard', shiftry: true, competitor: true },
        { feature: 'Multi-Location Support', shiftry: true, competitor: true },
        { feature: 'Payroll Integration', shiftry: true, competitor: true },
        { feature: 'Demand-Based Scheduling', shiftry: true, competitor: true },
        { feature: 'Predictive Staffing AI', shiftry: true, competitor: false },
        { feature: 'Easy Setup (< 15 min)', shiftry: true, competitor: false },
        { feature: 'Transparent Pricing', shiftry: true, competitor: false }
      ],
      pricing: { shiftry: '$2.50/user/mo', competitor: 'Custom pricing' }
    }
  };

  // Trust indicators and stats
  const trustStats = [
    { value: '10,000+', label: 'Organizations Worldwide' },
    { value: '500K+', label: 'Active Users Daily' },
    { value: '35%', label: 'Average Cost Savings' },
    { value: '4.9/5', label: 'Customer Rating' }
  ];

  // Customer testimonials
  const testimonials = [
    {
      quote: "Shiftry reduced our scheduling time from 6 hours to 30 minutes per week. The AI suggestions are incredibly accurate and the team loves the mobile app.",
      author: "Sarah Johnson",
      role: "Operations Manager",
      company: "HealthFirst Medical Group",
      industry: "Healthcare",
      avatar: "👩‍⚕️",
      rating: 5
    },
    {
      quote: "We tried When I Work and Deputy before Shiftry. The difference is night and day. Better features, lower price, and their support team is phenomenal.",
      author: "Michael Chen",
      role: "General Manager",
      company: "Urban Bistro Restaurant Chain",
      industry: "Hospitality",
      avatar: "👨‍🍳",
      rating: 5
    },
    {
      quote: "The facial recognition and GPS features eliminated timesheet fraud completely. We're saving thousands monthly and payroll processing is now effortless.",
      author: "Jennifer Martinez",
      role: "HR Director",
      company: "RetailPro Stores",
      industry: "Retail",
      avatar: "👩‍💼",
      rating: 5
    }
  ];

  // FAQs
  const faqs = [
    {
      question: "What makes Shiftry better than When I Work, Deputy, or Homebase?",
      answer: "Shiftry combines the best features of all competitors at a fraction of the cost. We offer AI-powered scheduling, facial recognition clock-in, advanced analytics, and built-in team communication—features that competitors either don't have or charge premium prices for. Plus, our pricing is 40% lower than When I Work and Deputy, with no hidden fees or per-location charges."
    },
    {
      question: "How does Shiftry's AI-powered scheduling work?",
      answer: "Our AI analyzes employee availability, skills, certifications, historical patterns, Employee laws, and your business demands to automatically create optimal schedules. It learns from your preferences over time, predicts staffing needs during peak periods, and suggests the best employees for each shift. This reduces scheduling time by 90% while improving coverage quality and employee satisfaction."
    },
    {
      question: "Can Shiftry handle multiple locations and departments?",
      answer: "Absolutely! Shiftry is built for enterprise-scale operations. Manage unlimited locations, departments, and teams from a single dashboard. Set location-specific rules, transfer employees between sites, track certification requirements by department, and analyze performance across your entire organization. Our multi-location features rival enterprise systems costing 10x more."
    },
    {
      question: "How does facial recognition prevent time theft and buddy punching?",
      answer: "When employees clock in via our mobile app, they take a selfie that's instantly verified against their profile photo using advanced facial recognition AI. Combined with GPS geofencing, this ensures employees are physically present at the work location. This two-factor verification has reduced time theft by an average of 95% for our customers, saving thousands annually."
    },
    {
      question: "Does Shiftry integrate with my existing payroll system?",
      answer: "Yes! Shiftry integrates seamlessly with 50+ popular payroll providers including ADP, Paychex, QuickBooks, Gusto, BambooHR, and many more. Timesheet data flows automatically from Shiftry to your payroll system, eliminating manual data entry and reducing payroll processing time by up to 80%. Our integrations are pre-built and take just minutes to set up."
    },
    {
      question: "What kind of analytics and reporting does Shiftry provide?",
      answer: "Shiftry offers enterprise-grade analytics that transform your workforce data into actionable insights. Track Employee costs in real-time, analyze attendance patterns, monitor productivity trends, forecast staffing needs, identify compliance risks, and generate custom reports for any metric. Our analytics dashboard is fully customizable and includes automated weekly/monthly reporting sent directly to your inbox."
    },
    {
      question: "How quickly can we get started with Shiftry?",
      answer: "Most organizations are fully up and running within 24-48 hours. Our setup wizard guides you through importing employees, creating locations/departments, configuring schedules, and setting permissions in under 15 minutes. We also offer free white-glove onboarding with dedicated support for enterprise accounts. Unlike competitors that require weeks of implementation, Shiftry is designed for instant productivity."
    },
    {
      question: "What mobile features are available for employees?",
      answer: "Our mobile app (iOS and Android) gives employees complete schedule control on the go. They can view upcoming shifts, clock in/out with facial recognition and GPS, request time off, swap shifts with coworkers, update availability, receive push notifications, communicate with managers, and access the company newsfeed. The app works offline and syncs automatically when reconnected."
    },
    {
      question: "Is Shiftry compliant with Employee laws and regulations?",
      answer: "Yes, Shiftry is built with compliance at its core. We automatically enforce Employee laws including overtime rules, break requirements, minor working hours restrictions, and shift spacing regulations. The system alerts you to potential violations before they occur and maintains complete audit trails for compliance reporting. We stay updated with federal, state, and local regulations across all 50 states."
    },
    {
      question: "What kind of customer support does Shiftry provide?",
      answer: "We offer 24/7 customer support via live chat, email, and phone—included free with every plan. Our average response time is under 2 minutes for chat and 30 minutes for email. We also provide extensive documentation, video tutorials, weekly webinars, and a dedicated success manager for enterprise accounts. Unlike competitors who charge extra for phone support, all support channels are always free."
    }
  ];

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (message) {
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const result = await subscribeToNewsletter(email);
      toast.success(result.message || 'Subscription successful!');
      setEmail('');
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || error.message || 'Subscription failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const scaleIn = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: { scale: 1, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-white">
      <LandingNav />

      {/* Hero Section - Enterprise Grade */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white pt-20 pb-32 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center"
          >
            {/* Trust badge */}
            <motion.div variants={fadeInUp} className="inline-flex items-center bg-white/10 backdrop-blur-md px-6 py-2 rounded-full mb-8">
              <Award className="w-5 h-5 mr-2 text-yellow-300" />
              <span className="text-sm font-semibold">#1 Rated Workforce Management Software 2026</span>
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
            >
              The Best Employee Scheduling
              <br />
              <span className="bg-gradient-to-r from-yellow-200 via-blue-200 to-purple-200 bg-clip-text text-transparent">
                Software for Modern Teams
              </span>
            </motion.h1>

            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-blue-100 mb-4 max-w-4xl mx-auto leading-relaxed"
            >
              Smarter than When I Work. More affordable than Deputy. More powerful than Homebase.
            </motion.p>

            <motion.p 
              variants={fadeInUp}
              className="text-lg text-blue-200 mb-10 max-w-3xl mx-auto"
            >
              AI-powered scheduling, advanced time tracking, facial recognition, GPS verification, and comprehensive analytics—all in one platform. Save 10+ hours weekly and reduce Employee costs by 35%.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/signup')}
                className="bg-white text-blue-600 px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center group"
              >
                Start Free 7-Day Trial
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-500/20 backdrop-blur-md border-2 border-white/30 text-white px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-500/30 transition-all flex items-center"
              >
                <PlayCircle className="mr-3 w-6 h-6" />
                Watch 2-Min Demo
              </motion.button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center items-center gap-8 text-blue-100 text-sm"
            >
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2 text-green-300" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2 text-green-300" />
                <span>Setup in under 15 minutes</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2 text-green-300" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-green-300" />
                <span>SOC 2 & GDPR Compliant</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Hero image/screenshot */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 relative z-10"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 shadow-2xl">
            <img 
              src="/mainimage_top.png" 
              alt="Shiftry Dashboard - Employee Scheduling Software" 
              className="w-full h-auto rounded-xl shadow-2xl"
            />
          </div>
        </motion.div>
      </section>

      {/* Trust Stats Section */}
      <section className="py-16 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {trustStats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Showcase Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need in One Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features designed to streamline your workforce management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: 'Smart Scheduling', desc: 'AI-powered shift planning that adapts to your needs', image: '/Schedule Page.png' },
              { icon: Users, title: 'Team Management', desc: 'Centralized control for your entire workforce', image: '/Admin Dashboard.png' },
              { icon: BarChart3, title: 'Advanced Analytics', desc: 'Real-time insights and comprehensive reports', image: '/Analyticspage.png' },
              { icon: MessageSquare, title: 'Team Communication', desc: 'Built-in messaging and colEmployeeation tools', image: '/Newsfeed.png' },
              { icon: Shield, title: 'Security & Compliance', desc: 'Enterprise-grade data protection', image: '/Admin Dashboard.png' },
              { icon: Zap, title: 'AI Automation', desc: 'Smart workforce optimization and predictions', image: '/AI page.png' }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-2xl transition-all cursor-pointer overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 mb-4">{feature.desc}</p>
                    <img src={feature.image} alt={feature.title} className="w-full rounded-lg opacity-80 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple setup process to get your team up and running
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Sign Up Free', desc: 'Create your account in seconds. No credit card required for your 30-day trial.' },
              { step: '02', title: 'Add Your Team', desc: 'Import employees via CSV or add them manually. Set up departments and roles.' },
              { step: '03', title: 'Start Scheduling', desc: 'Create your first schedule with our AI-powered tools and watch productivity soar.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative text-center"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full text-white text-2xl font-bold mb-6 shadow-xl">
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] border-t-2 border-dashed border-blue-300"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video/Demo Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                See Shiftry in Action
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Watch how leading companies transform their workforce management with our platform
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Reduce scheduling time by 75%',
                  'Eliminate time theft with GPS tracking',
                  'Cut Employee costs by up to 35%',
                  'Boost employee satisfaction'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center text-lg">
                    <CheckCircle2 className="w-6 h-6 text-green-300 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/contact')}
                className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-xl"
              >
                Schedule Your Demo
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white/10 backdrop-blur-sm p-2">
                <img src="/mainimage_top.png" alt="Shiftry Demo" className="w-full rounded-xl" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Competitor Comparison Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center bg-blue-100 text-blue-600 px-6 py-2 rounded-full mb-6 font-semibold">
              <Sparkles className="w-5 h-5 mr-2" />
              Feature Comparison
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Shiftry Beats the Competition
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare Shiftry with leading scheduling software—When I Work, Deputy, Homebase, and Humanity. More features, better value, happier teams.
            </p>
          </motion.div>

          {/* Comparison selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {Object.entries(competitorComparison).map(([key, comp]) => {
              const LogoIcon = comp.logo;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedComparison(key)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center ${
                    selectedComparison === key
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <LogoIcon className="w-6 h-6 mr-2" />
                  {comp.name}
                </button>
              );
            })}
          </div>

          {/* Comparison table */}
          <AnimatePresence mode="wait">
            {(() => {
              const comp = competitorComparison[selectedComparison];
              const CompLogo = comp.logo;
              return (
                <motion.div
                  key={selectedComparison}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200"
                >
                  {/* Header */}
                  <div className="grid grid-cols-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                    <div className="text-lg font-bold">Feature</div>
                    <div className="text-center text-lg font-bold flex items-center justify-center">
                      <Award className="w-6 h-6 mr-2 text-yellow-300" />
                      Shiftry
                    </div>
                    <div className="text-center text-lg font-bold flex items-center justify-center">
                      <CompLogo className="w-6 h-6 mr-2" />
                      {comp.name}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="divide-y divide-gray-200">
                    {comp.features.map((item, index) => (
                      <div key={index} className="grid grid-cols-3 p-4 hover:bg-gray-50 transition-colors">
                        <div className="font-medium text-gray-900">{item.feature}</div>
                        <div className="text-center">
                          {item.shiftry ? (
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                              <Check className="w-5 h-5 text-green-600" />
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
                              <X className="w-5 h-5 text-red-600" />
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          {item.competitor ? (
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                              <Check className="w-5 h-5 text-green-600" />
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
                              <X className="w-5 h-5 text-red-600" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Pricing row */}
                    <div className="grid grid-cols-3 p-6 bg-blue-50">
                      <div className="font-bold text-gray-900 text-lg">Starting Price</div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{comp.pricing.shiftry}</div>
                        <div className="text-xs text-gray-600">billed annually</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{comp.pricing.competitor}</div>
                        <div className="text-xs text-gray-600">billed annually</div>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-center text-white">
                    <h3 className="text-2xl font-bold mb-4">
                      Switch to Shiftry and Save 40% on Scheduling Software
                    </h3>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/signup')}
                      className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg shadow-xl"
                    >
                      Start Free Trial →
                    </motion.button>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </section>

      {/* Product Features Showcase with Screenshots */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete Workforce Management in One Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to schedule smarter, track time accurately, analyze performance, and keep your team connected.
            </p>
          </motion.div>

          {/* Tab navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {productFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <button
                  key={feature.id}
                  onClick={() => setActiveTab(feature.id)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center ${
                    activeTab === feature.id
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {feature.title.split(' ').slice(0, 2).join(' ')}
                </button>
              );
            })}
          </div>

          {/* Feature content */}
          <AnimatePresence mode="wait">
            {(() => {
              const activeFeature = productFeatures.find(f => f.id === activeTab);
              const Icon = activeFeature.icon;
              return (
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="grid md:grid-cols-2 gap-12 items-center"
                >
                  {/* Left: Screenshot */}
                  <div className="order-2 md:order-1">
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white rounded-2xl shadow-2xl p-4 border border-gray-200"
                    >
                      <img 
                        src={activeFeature.image} 
                        alt={activeFeature.title}
                        className="w-full h-auto rounded-xl"
                      />
                    </motion.div>

                    {/* Stats under image */}
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      <div className="bg-white rounded-xl p-4 shadow text-center">
                        <div className="text-2xl font-bold text-blue-600">{activeFeature.stats.time}</div>
                        <div className="text-xs text-gray-600 mt-1">Time Saved</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 shadow text-center">
                        <div className="text-2xl font-bold text-green-600">{activeFeature.stats.accuracy}</div>
                        <div className="text-xs text-gray-600 mt-1">Accuracy</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 shadow text-center">
                        <div className="text-2xl font-bold text-purple-600">{activeFeature.stats.adoption}</div>
                        <div className="text-xs text-gray-600 mt-1">User Rating</div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Details */}
                  <div className="order-1 md:order-2">
                    <motion.div
                      initial={{ x: 20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="inline-flex items-center bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-4">
                        <Icon className="w-5 h-5 mr-2" />
                        <span className="text-sm font-semibold">{activeFeature.id.toUpperCase()}</span>
                      </div>

                      <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                        {activeFeature.title}
                      </h3>
                      <p className="text-xl text-blue-600 font-semibold mb-6">
                        {activeFeature.subtitle}
                      </p>
                      <p className="text-gray-600 text-lg leading-relaxed mb-8">
                        {activeFeature.description}
                      </p>

                      <div className="space-y-4">
                        <h4 className="font-bold text-gray-900 text-lg mb-4">Key Benefits:</h4>
                        {activeFeature.benefits.map((benefit, index) => (
                          <motion.div
                            key={index}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                            className="flex items-start"
                          >
                            <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </motion.div>
                        ))}
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          const featureMap = {
                            'schedule': '/schedule-feature',
                            'timesheet': '/timesheet-feature',
                            'analytics': '/analytics-feature',
                            'admin': '/admin-feature',
                            'communication': '/communication-feature',
                            'ai': '/ai-feature'
                          };
                          navigate(featureMap[activeTab]);
                        }}
                        className="mt-8 bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center"
                      >
                        Learn More About {activeFeature.title.split(' ')[0]}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </motion.button>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Loved by Teams Worldwide
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Join thousands of organizations that switched to Shiftry and never looked back.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                  ))}
                </div>

                <p className="text-white text-lg mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center">
                  <div className="text-4xl mr-4">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold text-white">{testimonial.author}</div>
                    <div className="text-blue-200 text-sm">{testimonial.role}</div>
                    <div className="text-blue-300 text-sm">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Shiftry and how it compares to other scheduling software.
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex justify-between items-center p-6 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-bold text-gray-900 pr-8">{faq.question}</span>
                  <ChevronDown
                    className={`w-6 h-6 text-blue-600 transform transition-transform duration-300 flex-shrink-0 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-6xl font-bold mb-6">
              Ready to Transform Your Workforce Management?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto">
              Join 10,000+ organizations saving time and money with Shiftry. Start your free 7-day trial today—no credit card required.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/signup')}
                className="bg-white text-blue-600 px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center"
              >
                Start Free Trial Now
                <ArrowRight className="ml-3 w-6 h-6" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/contact')}
                className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
              >
                Schedule a Demo
              </motion.button>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center items-center gap-6 text-blue-100">
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>Free 7-day trial</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>Setup in 15 minutes</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>Cancel anytime</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Stay Updated with Shiftry</h3>
          <p className="text-lg text-gray-600 mb-8">
            Get the latest workforce management tips, product updates, and exclusive offers delivered to your inbox.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row justify-center items-center gap-4 max-w-lg mx-auto">
            <input
              type="email"
              value={email}
              onChange={handleChange}
              placeholder="Enter your email address"
              className="w-full sm:w-auto flex-grow px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-gray-800 shadow-sm"
              required
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
            </button>
          </form>
        </div>
      </section>

      <FooterLanding />
    </div>
  );
};

export default LandingPage;
